/**
 * TUVI - Detalle de Hoja de Vida
 * Vista completa con campos de pago, cuentas de cobro y comprobantes
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNotification } from '../../context/NotificationContext';
import client from '../../services/graphqlClient';
import { UPLOAD_URL } from '../../config';
import ComprobanteIngresoModal from './ComprobanteIngresoModal';
import './Cartera.css';

const HojaVidaDetalle = ({ hojaVidaId, onVolver }) => {
  const { success, error } = useNotification();
  const [hojaVida, setHojaVida] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editandoCampos, setEditandoCampos] = useState(false);
  const [camposEditados, setCamposEditados] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const [subiendoComprobante, setSubiendoComprobante] = useState(null);
  const [mostrarModalComprobante, setMostrarModalComprobante] = useState(null);
  const [comprobanteData, setComprobanteData] = useState({
    monto: '',
    descripcion: '',
    fecha_pago: new Date().toISOString().split('T')[0],
    archivo: null
  });
  const fileInputRef = useRef(null);
  const [dragComprobante, setDragComprobante] = useState(false);
  const [mostrarComprobanteIngreso, setMostrarComprobanteIngreso] = useState(false);
  // Edición en línea de la descripción de un comprobante
  const [editDesc, setEditDesc] = useState({ id: null, texto: '' });
  const cancelDescRef = useRef(false);
  // Pago al que se le está adjuntando el archivo (los registrados sin comprobante)
  const [adjuntandoId, setAdjuntandoId] = useState(null);

  // Estados para modal de cuenta de cobro
  const [modalCuentaCobro, setModalCuentaCobro] = useState({ visible: false, campo: null });
  const [cuentaCobroArchivo, setCuentaCobroArchivo] = useState(null);
  const [enviandoCuentaCobro, setEnviandoCuentaCobro] = useState(false);
  const [cuentasCobroDisponibles, setCuentasCobroDisponibles] = useState([]);
  const [cargandoCuentas, setCargandoCuentas] = useState(false);
  const [cuentaSeleccionada, setCuentaSeleccionada] = useState(null);
  const [busquedaCuentaCobro, setBusquedaCuentaCobro] = useState('');

  // Estado del modal "Generar nueva cuenta de cobro" desde el campo de cartera.
  // Prellena fecha/tipo/valor con los datos del campo y crea una CuentaCobro
  // vinculada al campo en un solo paso (sin pasar por el módulo de Documentos).
  const [modalGenerarCobro, setModalGenerarCobro] = useState({ visible: false, campo: null, fecha: '', tipo_pago: 'avance', valor_pago: 0 });
  const [generandoCobro, setGenerandoCobro] = useState(false);

  // Estados para modal de marcar como pagado
  const [modalMarcarPagado, setModalMarcarPagado] = useState({ visible: false, campo: null });
  const [comprobantePagoArchivo, setComprobantePagoArchivo] = useState(null);
  const [registrandoPago, setRegistrandoPago] = useState(false);

  // Estados para seguimiento editable
  const [editandoSeguimiento, setEditandoSeguimiento] = useState(false);
  const [seguimientoData, setSeguimientoData] = useState({});
  const [guardandoSeguimiento, setGuardandoSeguimiento] = useState(false);

  // Estados para tareas
  const [nuevaTarea, setNuevaTarea] = useState({ responsable: '', tarea: '' });
  const [agregandoTarea, setAgregandoTarea] = useState(false);
  const [editandoTarea, setEditandoTarea] = useState(null);
  const [tareaEditTexto, setTareaEditTexto] = useState('');

  // Estados para control administrativo editable
  const [editandoAdmin, setEditandoAdmin] = useState(false);
  const [adminData, setAdminData] = useState({});
  const [guardandoAdmin, setGuardandoAdmin] = useState(false);

  // Estado para agregar pago manual al historial
  const [modalPagoHistorial, setModalPagoHistorial] = useState(false);
  const [nuevoPago, setNuevoPago] = useState({ fecha_pago: '', monto: '' });
  const [agregandoPago, setAgregandoPago] = useState(false);

  // Estado para edicion inline de pagos del historial
  const [editandoPago, setEditandoPago] = useState(null); // id del pago en edicion
  const [pagoEditData, setPagoEditData] = useState({ monto: '', fecha_pago: '' });

  // Estado para edicion de descuentos
  const [editandoDescuentos, setEditandoDescuentos] = useState(false);
  const [descuentosData, setDescuentosData] = useState(0);

  useEffect(() => {
    if (hojaVidaId) {
      cargarHojaVida();
    }
  }, [hojaVidaId]);

  const cargarHojaVida = async () => {
    try {
      setLoading(true);
      const gqlR = await client.query(`query($id: Int!) {
        carteraDetalle(id: $id) {
          hoja_vida {
            id proyecto_id notas finalizado observacion pago_proxima_semana valor_ejecutado delta_caja descuentos
            poliza seguro deposito contrato_firmado llaves formatos_admin nota_extra fecha_inicio_obra fecha_fin_obra
            proyecto_nombre apartamento cliente_nombre cliente_email cliente_telefono valor_final subtotal aiu iva estado
            total_recibido total_esperado pendiente porcentaje_pagado
            campos {
              id hoja_vida_id nombre porcentaje valor_calculado valor_recibido orden es_predeterminado pendiente
              estado_cobro cuenta_cobro_archivo cuenta_cobro_fecha_envio comprobante_pago_archivo fecha_pago
              cuenta_cobro_id cuenta_cobro_consecutivo cuenta_cobro_estado
              comprobantes { id campo_pago_id descripcion monto nombre_archivo ruta_archivo tipo_archivo tamano fecha_pago }
            }
            pagos_historial { id hoja_vida_id numero_pago fecha_pago monto origen }
            tareas { id hoja_vida_id usuario_id nombre_responsable tarea completada estado created_at }
          }
        }
      }`, { id: hojaVidaId });
      if (gqlR.carteraDetalle?.hoja_vida) {
        setHojaVida(gqlR.carteraDetalle.hoja_vida);
        setCamposEditados(gqlR.carteraDetalle.hoja_vida.campos || []);
      } else {
        error('Hoja de vida no encontrada');
      }
    } catch (err) {
      console.error('Error cargando hoja de vida:', err);
      error('Error al cargar la hoja de vida');
    } finally {
      setLoading(false);
    }
  };


  // ============================================
  // FUNCIONES DE SEGUIMIENTO
  // ============================================

  const iniciarEdicionSeguimiento = () => {
    setSeguimientoData({
      observacion: hojaVida.observacion || '',
      pago_proxima_semana: hojaVida.pago_proxima_semana || 0,
      nota_extra: hojaVida.nota_extra || ''
    });
    setEditandoSeguimiento(true);
  };

  const guardarSeguimiento = async () => {
    setGuardandoSeguimiento(true);
    try {
      const gqlR = await client.mutate(`mutation($hid: Int!, $input: SeguimientoCarteraInput!) { actualizarSeguimientoCartera(hoja_vida_id: $hid, input: $input) { success message } }`, { hid: hojaVida.id, input: seguimientoData });
      if (gqlR.actualizarSeguimientoCartera.success) {
        success('Seguimiento actualizado');
        setEditandoSeguimiento(false);
        cargarHojaVida();
      } else {
        error('Error al actualizar');
      }
    } catch (err) {
      error('Error al guardar seguimiento');
    } finally {
      setGuardandoSeguimiento(false);
    }
  };

  // ============================================
  // FUNCIONES DE CONTROL ADMINISTRATIVO
  // ============================================

  const iniciarEdicionAdmin = () => {
    setAdminData({
      poliza: hojaVida.poliza || '',
      seguro: hojaVida.seguro || '',
      deposito: hojaVida.deposito || '',
      contrato_firmado: hojaVida.contrato_firmado || '',
      llaves: hojaVida.llaves || '',
      formatos_admin: hojaVida.formatos_admin || '',
      fecha_inicio_obra: hojaVida.fecha_inicio_obra || '',
      fecha_fin_obra: hojaVida.fecha_fin_obra || ''
    });
    setEditandoAdmin(true);
  };

  const guardarAdmin = async () => {
    setGuardandoAdmin(true);
    try {
      const gqlR = await client.mutate(`mutation($hid: Int!, $input: SeguimientoCarteraInput!) { actualizarSeguimientoCartera(hoja_vida_id: $hid, input: $input) { success message } }`, { hid: hojaVida.id, input: adminData });
      if (gqlR.actualizarSeguimientoCartera.success) {
        success('Control administrativo actualizado');
        setEditandoAdmin(false);
        cargarHojaVida();
      } else {
        error('Error al actualizar');
      }
    } catch (err) {
      error('Error al guardar control administrativo');
    } finally {
      setGuardandoAdmin(false);
    }
  };

  // ============================================
  // FUNCIONES DE TAREAS
  // ============================================

  const agregarTarea = async () => {
    if (!nuevaTarea.tarea.trim()) {
      error('La tarea no puede estar vacia');
      return;
    }
    if (!nuevaTarea.responsable) {
      error('Selecciona un responsable');
      return;
    }
    setAgregandoTarea(true);
    try {
      const gqlR = await client.mutate(`mutation($hid: Int!, $tarea: String!, $resp: String) { crearTareaCartera(hoja_vida_id: $hid, tarea: $tarea, nombre_responsable: $resp) { success message } }`, { hid: hojaVida.id, tarea: nuevaTarea.tarea, resp: nuevaTarea.responsable });
      if (gqlR.crearTareaCartera.success) {
        success('Tarea agregada');
        setNuevaTarea({ responsable: '', tarea: '' });
        cargarHojaVida();
      } else {
        error('Error al agregar tarea');
      }
    } catch (err) {
      error('Error al agregar tarea');
    } finally {
      setAgregandoTarea(false);
    }
  };

  const iniciarEdicionTarea = (tarea) => {
    setEditandoTarea(tarea.id);
    setTareaEditTexto(tarea.tarea);
  };

  const guardarEdicionTarea = async (tareaId) => {
    if (!tareaEditTexto.trim()) {
      error('La tarea no puede estar vacia');
      return;
    }
    try {
      const gqlR = await client.mutate(`mutation($id: Int!, $estado: String!) { actualizarEstadoTareaCartera(id: $id, estado: $estado) { success message } }`, { id: tareaId, estado: 'pendiente' });
      // Also we need to handle text edit - for now just update status
      if (gqlR.actualizarEstadoTareaCartera.success) {
        success('Tarea actualizada');
        setEditandoTarea(null);
        cargarHojaVida();
      } else {
        error('Error al actualizar tarea');
      }
    } catch (err) {
      error('Error al actualizar tarea');
    }
  };

  const eliminarTarea = async (tareaId) => {
    if (!window.confirm('¿Eliminar esta tarea?')) return;
    try {
      const gqlR = await client.mutate(`mutation($id: Int!) { eliminarTareaCartera(id: $id) { success } }`, { id: tareaId });
      if (gqlR.eliminarTareaCartera.success) {
        success('Tarea eliminada');
        cargarHojaVida();
      }
    } catch (err) {
      error('Error al eliminar tarea');
    }
  };

  // ============================================
  // FUNCIONES DE PAGO HISTORIAL
  // ============================================

  const agregarPagoHistorial = async () => {
    if (!nuevoPago.monto || parseFloat(nuevoPago.monto) <= 0) {
      error('Ingresa un monto valido');
      return;
    }
    setAgregandoPago(true);
    try {
      const gqlR = await client.mutate(`mutation($hid: Int!, $monto: Float!, $fecha: String) { agregarPagoCartera(hoja_vida_id: $hid, monto: $monto, fecha_pago: $fecha) { success message } }`, { hid: hojaVida.id, monto: parseFloat(nuevoPago.monto), fecha: nuevoPago.fecha_pago || null });
      if (gqlR.agregarPagoCartera.success) {
        success('Pago registrado en historial');
        setModalPagoHistorial(false);
        setNuevoPago({ fecha_pago: '', monto: '' });
        cargarHojaVida();
      } else {
        error('Error al registrar pago');
      }
    } catch (err) {
      error('Error al registrar pago');
    } finally {
      setAgregandoPago(false);
    }
  };

  // ============================================
  // FUNCIONES DE EDICION DE PAGOS
  // ============================================

  const iniciarEdicionPago = (pago) => {
    setEditandoPago(pago.id);
    setPagoEditData({
      monto: pago.monto,
      fecha_pago: pago.fecha_pago || ''
    });
  };

  const guardarEdicionPago = async (pagoId) => {
    try {
      const gqlR = await client.mutate(`mutation($id: Int!, $monto: Float!, $fecha: String) { editarPagoCartera(id: $id, monto: $monto, fecha_pago: $fecha) { success message } }`, { id: pagoId, monto: parseFloat(pagoEditData.monto) || 0, fecha: pagoEditData.fecha_pago || null });
      if (gqlR.editarPagoCartera.success) {
        success('Pago actualizado');
        setEditandoPago(null);
        cargarHojaVida();
      } else {
        error('Error al actualizar pago');
      }
    } catch (err) {
      error('Error al actualizar pago');
    }
  };

  const eliminarPagoHistorial = async (pagoId) => {
    if (!window.confirm('¿Eliminar este pago del historial?')) return;
    try {
      const gqlR = await client.mutate(`mutation($id: Int!) { eliminarPagoCartera(id: $id) { success } }`, { id: pagoId });
      if (gqlR.eliminarPagoCartera.success) {
        success('Pago eliminado');
        cargarHojaVida();
      } else {
        error('Error al eliminar');
      }
    } catch (err) {
      error('Error al eliminar pago');
    }
  };

  // ============================================
  // FUNCIONES DE DESCUENTOS
  // ============================================

  const iniciarEdicionDescuentos = () => {
    setDescuentosData(hojaVida.descuentos || 0);
    setEditandoDescuentos(true);
  };

  const guardarDescuentos = async () => {
    try {
      const gqlR = await client.mutate(`mutation($hid: Int!, $input: SeguimientoCarteraInput!) { actualizarSeguimientoCartera(hoja_vida_id: $hid, input: $input) { success } }`, { hid: hojaVida.id, input: { descuentos: parseFloat(descuentosData) || 0 } });
      if (gqlR.actualizarSeguimientoCartera.success) {
        success('Descuentos actualizado');
        setEditandoDescuentos(false);
        cargarHojaVida();
      } else {
        error('Error al actualizar');
      }
    } catch (err) {
      error('Error al guardar descuentos');
    }
  };

  // ============================================
  // FUNCIONES DE ESTADO DE TAREAS
  // ============================================

  const cambiarEstadoTarea = async (tareaId, nuevoEstado) => {
    try {
      const gqlR = await client.mutate(`mutation($id: Int!, $estado: String!) { actualizarEstadoTareaCartera(id: $id, estado: $estado) { success } }`, { id: tareaId, estado: nuevoEstado });
      if (gqlR.actualizarEstadoTareaCartera.success) {
        cargarHojaVida();
      }
    } catch (err) {
      error('Error al actualizar tarea');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    // Acepta tanto 'YYYY-MM-DD' (le añade hora local para evitar el corrimiento
    // de zona horaria) como timestamps ISO completos del backend.
    const date = String(dateStr).includes('T')
      ? new Date(dateStr)
      : new Date(dateStr + 'T00:00:00');
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('es-CO', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-CO', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Manejo de campos
  const handleCampoChange = (index, field, value) => {
    const nuevos = [...camposEditados];
    nuevos[index] = { ...nuevos[index], [field]: value };
    setCamposEditados(nuevos);
  };

  const agregarCampo = () => {
    if (camposEditados.length >= 8) {
      error('Maximo 8 campos permitidos');
      return;
    }
    setCamposEditados([
      ...camposEditados,
      { nombre: '', porcentaje: 0, es_predeterminado: false }
    ]);
  };

  const eliminarCampo = (index) => {
    if (camposEditados[index].es_predeterminado) {
      error('No se pueden eliminar los campos predeterminados');
      return;
    }
    setCamposEditados(camposEditados.filter((_, i) => i !== index));
  };

  const calcularTotalPorcentaje = () => {
    return camposEditados.reduce((sum, c) => sum + parseFloat(c.porcentaje || 0), 0);
  };

  const guardarCampos = async () => {
    const total = calcularTotalPorcentaje();
    if (Math.abs(total - 100) > 0.01) {
      error(`Los porcentajes deben sumar 100%. Actualmente suman ${total.toFixed(2)}%`);
      return;
    }

    if (camposEditados.some(c => !c.nombre.trim())) {
      error('Todos los campos deben tener un nombre');
      return;
    }

    setGuardando(true);
    try {
      // El input del schema es CampoPagoInput y solo acepta { id, nombre, porcentaje }.
      // El "orden" lo calcula el backend según la posición en el arreglo.
      const gqlR = await client.mutate(`mutation($hvid: Int!, $campos: [CampoPagoInput!]!) { actualizarCamposCartera(hoja_vida_id: $hvid, campos: $campos) { success message } }`, {
        hvid: hojaVida.id,
        campos: camposEditados.map(c => ({ id: c.id || null, nombre: c.nombre, porcentaje: parseFloat(c.porcentaje) || 0 }))
      });
      const data = gqlR.actualizarCamposCartera;

      if (data.success) {
        success('Campos actualizados');
        setEditandoCampos(false);
        cargarHojaVida();
      } else {
        error('Error al actualizar');
      }
    } catch (err) {
      error('Error al guardar los campos');
    } finally {
      setGuardando(false);
    }
  };

  // ============================================
  // FUNCIONES DE CUENTA DE COBRO
  // ============================================

  const abrirModalCuentaCobro = async (campo) => {
    if (!hojaVida.cliente_email) {
      error('El cliente no tiene correo electronico registrado');
      return;
    }
    setCuentaCobroArchivo(null);
    setCuentaSeleccionada(null);
    setModalCuentaCobro({ visible: true, campo });

    // Cargar cuentas de cobro disponibles del proyecto
    setCargandoCuentas(true);
    try {
      const gqlData = await client.query(`{ cuentasCobro { id consecutivo fecha nombre_cliente proyecto_id proyecto_nombre apartamento valor_pago } }`);
      const data = { success: true, cuentas: gqlData.cuentasCobro || [] };
      if (data.success) {
        // Filtrar por proyecto actual
        const cuentasProyecto = data.cuentas.filter(c =>
          c.proyecto_id && parseInt(c.proyecto_id) === parseInt(hojaVida.proyecto_id)
        );
        // Si no hay del proyecto, mostrar todas
        setCuentasCobroDisponibles(cuentasProyecto.length > 0 ? cuentasProyecto : data.cuentas);
      }
    } catch (err) {
      console.error('Error cargando cuentas de cobro:', err);
    } finally {
      setCargandoCuentas(false);
    }
  };

  const enviarCuentaCobro = async () => {
    if (!cuentaSeleccionada) {
      error('Debes seleccionar una cuenta de cobro');
      return;
    }

    setEnviandoCuentaCobro(true);
    try {
      const gqlR = await client.mutate(`mutation($cpid: Int!, $ccid: Int!) { enviarCuentaCobroCartera(campo_pago_id: $cpid, cuenta_cobro_id: $ccid) { success message } }`, {
        cpid: modalCuentaCobro.campo.id, ccid: cuentaSeleccionada.id
      });
      const data = gqlR.enviarCuentaCobroCartera;

      if (data.success) {
        success(`Cuenta de cobro enviada a ${hojaVida.cliente_email}`);
        setModalCuentaCobro({ visible: false, campo: null });
        setCuentaSeleccionada(null);
        cargarHojaVida();
      } else {
        error(data.message || 'Error al enviar cuenta de cobro');
      }
    } catch (err) {
      error('Error al enviar la cuenta de cobro');
    } finally {
      setEnviandoCuentaCobro(false);
    }
  };

  // Abre el modal para generar una nueva cuenta de cobro desde el campo de
  // cartera. Pre-llena fecha (hoy), tipo_pago deducido del nombre del campo y
  // valor_pago = saldo pendiente del campo (valor_calculado - valor_recibido).
  const abrirModalGenerarCobro = (campo) => {
    const hoy = new Date().toISOString().slice(0, 10);
    const nombre = String(campo.nombre || '').toUpperCase();
    let tipo = 'avance';
    if (nombre.includes('SEPARAC')) tipo = 'separacion';
    else if (nombre.includes('ANTICIPO') || campo.orden === 1) tipo = 'anticipo';
    else if (nombre.includes('FINAL') || nombre.includes('ENTREGA') || nombre.includes('SALDO')) tipo = 'entrega_final';
    const saldo = Math.max(0, Number(campo.valor_calculado || 0) - Number(campo.valor_recibido || 0));
    setModalGenerarCobro({ visible: true, campo, fecha: hoy, tipo_pago: tipo, valor_pago: saldo });
  };

  const confirmarGenerarCobro = async () => {
    const { campo, fecha, tipo_pago, valor_pago } = modalGenerarCobro;
    if (!campo) return;
    if (!fecha) { error('La fecha es requerida'); return; }
    if (!valor_pago || valor_pago <= 0) { error('El valor debe ser mayor a cero'); return; }
    setGenerandoCobro(true);
    try {
      const gqlR = await client.mutate(
        `mutation($cpid: Int!, $fecha: String, $tipo: String, $valor: Float) {
          generarCuentaCobroDesdeCampo(campo_pago_id: $cpid, fecha: $fecha, tipo_pago: $tipo, valor_pago: $valor) {
            success message cuenta_cobro_id consecutivo
          }
        }`,
        { cpid: campo.id, fecha, tipo: tipo_pago, valor: Number(valor_pago) }
      );
      const data = gqlR.generarCuentaCobroDesdeCampo;
      if (data.success) {
        success(data.message);
        setModalGenerarCobro({ visible: false, campo: null, fecha: '', tipo_pago: 'avance', valor_pago: 0 });
        cargarHojaVida();
      } else {
        error(data.message || 'Error al generar la cuenta de cobro');
      }
    } catch (err) {
      console.error(err);
      error('Error de conexión al generar la cuenta de cobro');
    } finally {
      setGenerandoCobro(false);
    }
  };

  // ============================================
  // FUNCIONES DE MARCAR COMO PAGADO
  // ============================================

  const abrirModalMarcarPagado = (campo) => {
    setComprobantePagoArchivo(null);
    setModalMarcarPagado({ visible: true, campo });
  };

  const registrarPagoCuentaCobro = async () => {
    if (!comprobantePagoArchivo) {
      error('Debes seleccionar el comprobante de pago');
      return;
    }

    setRegistrandoPago(true);
    try {
      // Subir comprobante a MinIO
      let comprobanteKey = null;
      if (comprobantePagoArchivo) {
        const fd = new FormData();
        fd.append('files', comprobantePagoArchivo);
        const token = localStorage.getItem('tuvi_token');
        const upRes = await fetch(`${UPLOAD_URL}/catalogo/upload?prefix=cartera/pagos/&bucket=comprobantes`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: fd });
        const upData = await upRes.json();
        if (upData.files?.[0]?.key) comprobanteKey = upData.files[0].key;
      }

      const gqlR = await client.mutate(`mutation($cpid: Int!, $key: String, $desc: String) { registrarPagoCuentaCobroCartera(campo_pago_id: $cpid, comprobante_key: $key, descripcion: $desc) { success message } }`, {
        cpid: modalMarcarPagado.campo.id, key: comprobanteKey, desc: 'Pago de cuenta de cobro'
      });
      const data = gqlR.registrarPagoCuentaCobroCartera;

      if (data.success) {
        success('Pago registrado exitosamente');
        setModalMarcarPagado({ visible: false, campo: null });
        setComprobantePagoArchivo(null);
        cargarHojaVida();
      } else {
        error('Error al registrar pago');
      }
    } catch (err) {
      error('Error al registrar el pago');
    } finally {
      setRegistrandoPago(false);
    }
  };

  // ============================================
  // FUNCIONES DE COMPROBANTES (existentes)
  // ============================================

  const handleFileChange = (e) => {
    if (e.target.files[0]) {
      setComprobanteData(prev => ({ ...prev, archivo: e.target.files[0] }));
    }
  };

  // Acepta un archivo de comprobante venga de donde venga (selector, pegar o arrastrar)
  const aceptarArchivoComprobante = useCallback((file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      error('El archivo es muy grande. Máximo 10MB');
      return;
    }
    setComprobanteData(prev => ({ ...prev, archivo: file }));
  }, [error]);

  const handleDropComprobante = (e) => {
    e.preventDefault();
    setDragComprobante(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) aceptarArchivoComprobante(file);
  };

  // Pegar imagen con Ctrl+V mientras el modal de Registrar Pago está abierto
  // (listener global: funciona sin tener que hacer clic dentro de un campo).
  useEffect(() => {
    if (!mostrarModalComprobante) return;
    const onPaste = (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const item of items) {
        if (item.type.startsWith('image/')) {
          const blob = item.getAsFile();
          if (blob) {
            const ext = blob.type.split('/')[1] || 'png';
            aceptarArchivoComprobante(new File([blob], `comprobante_${Date.now()}.${ext}`, { type: blob.type }));
          }
          e.preventDefault();
          return;
        }
      }
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [mostrarModalComprobante, aceptarArchivoComprobante]);

  const subirComprobante = async (campoId) => {
    // El comprobante (archivo) es OPCIONAL: se puede registrar el pago solo con
    // el monto y adjuntar el soporte después.
    if (!comprobanteData.monto || parseFloat(comprobanteData.monto) <= 0) {
      error('Ingresa un monto valido');
      return;
    }

    setSubiendoComprobante(campoId);
    try {
      // Subir archivo a MinIO
      let archivoKey = null;
      if (comprobanteData.archivo) {
        const fd = new FormData();
        fd.append('files', comprobanteData.archivo);
        const token = localStorage.getItem('tuvi_token');
        const upRes = await fetch(`${UPLOAD_URL}/catalogo/upload?prefix=cartera/comprobantes/&bucket=comprobantes`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: fd });
        const upData = await upRes.json();
        if (upData.files?.[0]?.key) archivoKey = upData.files[0].key;
      }

      const gqlR = await client.mutate(`mutation($cpid: Int!, $monto: Float!, $desc: String, $fecha: String, $key: String, $nombre: String) { subirComprobanteCartera(campo_pago_id: $cpid, monto: $monto, descripcion: $desc, fecha_pago: $fecha, archivo_key: $key, nombre_archivo: $nombre) { success message } }`, {
        cpid: campoId, monto: parseFloat(comprobanteData.monto),
        desc: comprobanteData.descripcion, fecha: comprobanteData.fecha_pago,
        key: archivoKey, nombre: comprobanteData.archivo?.name || 'comprobante',
      });
      const data = gqlR.subirComprobanteCartera;

      if (data.success) {
        success('Comprobante registrado');
        setMostrarModalComprobante(null);
        setComprobanteData({
          monto: '',
          descripcion: '',
          fecha_pago: new Date().toISOString().split('T')[0],
          archivo: null
        });
        cargarHojaVida();
      } else {
        error(data.message || 'Error al subir comprobante');
      }
    } catch (err) {
      error('Error al subir el comprobante');
    } finally {
      setSubiendoComprobante(null);
    }
  };

  // Guarda la descripción editada de un comprobante (Enter o al salir del campo)
  const guardarDescripcionComprobante = async (comprobanteId, texto) => {
    if (cancelDescRef.current) { cancelDescRef.current = false; setEditDesc({ id: null, texto: '' }); return; }
    setEditDesc({ id: null, texto: '' });
    try {
      const gqlR = await client.mutate(
        `mutation($id: Int!, $d: String) { editarDescripcionComprobante(id: $id, descripcion: $d) { success message } }`,
        { id: comprobanteId, d: texto }
      );
      if (gqlR.editarDescripcionComprobante.success) {
        success('Descripción actualizada');
        cargarHojaVida();
      } else {
        error(gqlR.editarDescripcionComprobante.message || 'Error al actualizar la descripción');
      }
    } catch (err) {
      error('Error al actualizar la descripción');
    }
  };

  // Adjuntar el archivo a un pago que quedó registrado sin comprobante: sube el
  // archivo igual que al registrar el pago y lo enlaza, sin tocar monto ni fecha.
  const adjuntarArchivoComprobante = async (comp, file) => {
    if (!file) return;
    setAdjuntandoId(comp.id);
    try {
      const fd = new FormData();
      fd.append('files', file);
      const token = localStorage.getItem('tuvi_token');
      const upRes = await fetch(`${UPLOAD_URL}/catalogo/upload?prefix=cartera/comprobantes/&bucket=comprobantes`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: fd });
      const upData = await upRes.json();
      const key = upData.files?.[0]?.key;
      if (!key) { error(upData.message || 'No se pudo subir el archivo'); return; }
      const gqlR = await client.mutate(
        `mutation($id: Int!, $k: String!, $n: String, $t: String, $s: Int) { adjuntarArchivoComprobanteCartera(id: $id, archivo_key: $k, nombre_archivo: $n, tipo_archivo: $t, tamano: $s) { success message } }`,
        { id: comp.id, k: key, n: file.name, t: file.type || null, s: file.size || null }
      );
      if (gqlR.adjuntarArchivoComprobanteCartera.success) {
        success('Comprobante adjuntado');
        cargarHojaVida();
      } else {
        error(gqlR.adjuntarArchivoComprobanteCartera.message || 'No se pudo adjuntar el comprobante');
      }
    } catch (err) {
      error('Error al adjuntar el comprobante');
    } finally {
      setAdjuntandoId(null);
    }
  };

  const eliminarComprobante = async (comprobanteId) => {
    if (!window.confirm('¿Eliminar este comprobante?')) return;

    try {
      const gqlR = await client.mutate(`mutation($id: Int!) { eliminarComprobanteCartera(id: $id) { success message } }`, { id: comprobanteId });
      const data = gqlR.eliminarComprobanteCartera;

      if (data.success) {
        success('Comprobante eliminado');
        cargarHojaVida();
      } else {
        error('Error al eliminar');
      }
    } catch (err) {
      error('Error al eliminar el comprobante');
    }
  };

  const getFileIcon = (tipo) => {
    if (tipo?.includes('image')) return '🖼️';
    if (tipo?.includes('pdf')) return '📄';
    return '📎';
  };

  // Estado real del campo. Si quedó "pagado" pero el valor esperado subió (un
  // adicional, un cambio de valor), o tiene abonos sin completar, es un pago
  // parcial: tiene saldo y se debe poder seguir registrando pagos.
  const estadoEfectivo = (campo) => {
    const recibido = Number(campo.valor_recibido || 0);
    const pendiente = Number(campo.valor_calculado || 0) - recibido;
    if (pendiente > 1 && recibido > 0 && (campo.estado_cobro === 'pagado' || campo.estado_cobro === 'sin_enviar')) return 'parcial';
    return campo.estado_cobro;
  };

  const getEstadoCobroBadge = (campo) => {
    switch (estadoEfectivo(campo)) {
      case 'pendiente_pago':
        return <span className="estado-cobro-badge pendiente">Pendiente de Pago</span>;
      case 'parcial':
        return <span className="estado-cobro-badge pendiente">Pago parcial</span>;
      case 'pagado':
        return <span className="estado-cobro-badge pagado">Pagado</span>;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="cartera-loading">
        <div className="spinner"></div>
        <p>Cargando hoja de vida...</p>
      </div>
    );
  }

  if (!hojaVida) {
    return (
      <div className="cartera-error">
        <p>Hoja de vida no encontrada</p>
        <button onClick={onVolver}>Volver a Cartera</button>
      </div>
    );
  }

  return (
    <div className="hoja-vida-detalle">
      {/* Header */}
      <div className="hv-detalle-header">
        <button className="btn-volver" onClick={onVolver}>
          ← Volver
        </button>
        <div className="hv-titulo">
          <h1>{hojaVida.cliente_nombre}</h1>
          <span className="proyecto-nombre">{hojaVida.proyecto_nombre}</span>
        </div>
        <button className="btn-comprobante-ingreso" onClick={() => setMostrarComprobanteIngreso(true)}>
          📄 Comprobante de Ingresos
        </button>
      </div>

      {/* Info del cliente */}
      <div className="hv-info-cliente">
        <div className="info-item">
          <span className="label">Email</span>
          <span className="valor">{hojaVida.cliente_email || '-'}</span>
        </div>
        <div className="info-item">
          <span className="label">Telefono</span>
          <span className="valor">{hojaVida.cliente_telefono || '-'}</span>
        </div>
        <div className="info-item">
          <span className="label">Valor Proyecto</span>
          <span className="valor">{formatCurrency(hojaVida.valor_final)}</span>
        </div>
        <div className="info-item">
          <span className="label">Estado</span>
          <span className={`estado-badge ${hojaVida.estado}`}>
            {hojaVida.estado?.replace(/_/g, ' ')}
          </span>
        </div>
      </div>

      {/* Resumen de pagos */}
      <div className="hv-resumen-pagos">
        <div className="resumen-item esperado">
          <span className="valor">{formatCurrency(hojaVida.total_esperado)}</span>
          <span className="label">Total Esperado</span>
        </div>
        <div className="resumen-item recibido">
          <span className="valor">{formatCurrency(hojaVida.total_recibido)}</span>
          <span className="label">Total Recibido</span>
        </div>
        <div className="resumen-item pendiente">
          <span className="valor">{formatCurrency(hojaVida.pendiente)}</span>
          <span className="label">Pendiente</span>
        </div>
        <div className="resumen-item progreso">
          <span className="valor">{hojaVida.porcentaje_pagado.toFixed(1)}%</span>
          <span className="label">Progreso</span>
          <div className="mini-barra">
            <div
              className="mini-fill"
              style={{ width: `${Math.min(hojaVida.porcentaje_pagado, 100)}%` }}
            ></div>
          </div>
        </div>
      </div>

      {/* Campos de pago */}
      <div className="hv-campos-section">
        <div className="campos-header">
          <h2>Campos de Pago</h2>
          {!editandoCampos ? (
            <button className="btn-editar-campos" onClick={() => setEditandoCampos(true)}>
              Editar Campos
            </button>
          ) : (
            <div className="campos-acciones">
              <span className={`total-porcentaje ${Math.abs(calcularTotalPorcentaje() - 100) < 0.01 ? 'ok' : 'error'}`}>
                Total: {calcularTotalPorcentaje().toFixed(2)}%
              </span>
              {camposEditados.length < 8 && (
                <button className="btn-agregar-campo" onClick={agregarCampo}>
                  + Agregar Campo
                </button>
              )}
              <button className="btn-cancelar" onClick={() => {
                setEditandoCampos(false);
                setCamposEditados(hojaVida.campos || []);
              }}>
                Cancelar
              </button>
              <button className="btn-guardar" onClick={guardarCampos} disabled={guardando}>
                {guardando ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          )}
        </div>

        <div className="campos-lista">
          {(editandoCampos ? camposEditados : hojaVida.campos).map((campo, index) => (
            <div key={campo.id || index} className={`campo-pago-card ${!editandoCampos && estadoEfectivo(campo) === 'pagado' ? 'campo-pagado' : ''}`}>
              {editandoCampos ? (
                <div className="campo-edicion">
                  <input
                    type="text"
                    value={campo.nombre}
                    onChange={(e) => handleCampoChange(index, 'nombre', e.target.value)}
                    placeholder="Nombre del campo"
                  />
                  <div className="campo-porcentaje-edit">
                    <input
                      type="number"
                      value={campo.porcentaje}
                      onChange={(e) => handleCampoChange(index, 'porcentaje', parseFloat(e.target.value) || 0)}
                      min="0"
                      max="100"
                      step="0.5"
                    />
                    <span>%</span>
                  </div>
                  {!campo.es_predeterminado && (
                    <button
                      className="btn-eliminar-campo"
                      onClick={() => eliminarCampo(index)}
                      title="Eliminar campo"
                    >
                      X
                    </button>
                  )}
                </div>
              ) : (
                <>
                  <div className="campo-header">
                    <div className="campo-info">
                      <h3>{campo.nombre}</h3>
                      <span className="campo-porcentaje">{campo.porcentaje}%</span>
                      {getEstadoCobroBadge(campo)}
                    </div>
                    <div className="campo-acciones-btns">
                      {/* Boton generar nueva cuenta de cobro (link 1-a-1 con el campo) */}
                      {(campo.estado_cobro === 'sin_enviar' || estadoEfectivo(campo) === 'parcial') && !campo.cuenta_cobro_id && (
                        <button
                          className="btn-enviar-cuenta"
                          onClick={() => abrirModalGenerarCobro(campo)}
                          title="Generar y vincular una cuenta de cobro nueva al campo"
                        >
                          Generar Cuenta de Cobro
                        </button>
                      )}
                      {/* Boton vincular cuenta existente */}
                      {(campo.estado_cobro === 'sin_enviar' || estadoEfectivo(campo) === 'parcial') && !campo.cuenta_cobro_id && (
                        <button
                          className="btn-enviar-cuenta"
                          style={{ background: '#64748b' }}
                          onClick={() => abrirModalCuentaCobro(campo)}
                          title="Enviar una cuenta de cobro existente"
                        >
                          Vincular Existente
                        </button>
                      )}
                      {/* Boton marcar como pagado */}
                      {campo.estado_cobro === 'pendiente_pago' && (
                        <button
                          className="btn-marcar-pagado"
                          onClick={() => abrirModalMarcarPagado(campo)}
                          title="Marcar como pagado"
                        >
                          Marcar como Pagado
                        </button>
                      )}
                      {/* Boton registrar pago manual: visible mientras el campo tenga saldo,
                          aunque haya quedado marcado "pagado" antes de un adicional */}
                      {estadoEfectivo(campo) !== 'pagado' && (
                        <button
                          className="btn-agregar-comprobante"
                          onClick={() => setMostrarModalComprobante(campo.id)}
                        >
                          + Registrar Pago
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="campo-valores">
                    <div className="campo-valor">
                      <span className="label">Esperado</span>
                      <span className="valor">{formatCurrency(campo.valor_calculado)}</span>
                    </div>
                    <div className="campo-valor">
                      <span className="label">Recibido</span>
                      <span className="valor recibido">{formatCurrency(campo.valor_recibido)}</span>
                    </div>
                    <div className="campo-valor">
                      <span className="label">Pendiente</span>
                      <span className="valor pendiente">{formatCurrency(campo.pendiente)}</span>
                    </div>
                  </div>

                  <div className="campo-progreso">
                    <div className="progreso-barra">
                      <div
                        className="progreso-fill"
                        style={{
                          width: `${Math.min((campo.valor_recibido / campo.valor_calculado) * 100, 100)}%`
                        }}
                      ></div>
                    </div>
                  </div>

                  {/* Vínculo a cuenta de cobro (FK real campo_pago_id) */}
                  {campo.cuenta_cobro_id && (
                    <div className="cuenta-cobro-info">
                      <div className="info-envio">
                        <span className="label">Cuenta de cobro vinculada:</span>
                        <span className="valor">#{campo.cuenta_cobro_consecutivo}</span>
                        <span className="valor" style={{
                          textTransform: 'uppercase', fontSize: '0.7rem', padding: '2px 8px', borderRadius: '6px',
                          background: campo.cuenta_cobro_estado === 'pagada' ? '#d1fae5' : campo.cuenta_cobro_estado === 'enviada' ? '#fef3c7' : '#e2e8f0',
                          color: campo.cuenta_cobro_estado === 'pagada' ? '#065f46' : campo.cuenta_cobro_estado === 'enviada' ? '#92400e' : '#475569',
                        }}>{campo.cuenta_cobro_estado || 'borrador'}</span>
                        <a href={`/cuentas-cobro?id=${campo.cuenta_cobro_id}`} target="_blank" rel="noopener noreferrer" className="ver-documento">
                          Abrir cuenta
                        </a>
                      </div>
                    </div>
                  )}

                  {/* Info de cuenta de cobro enviada */}
                  {campo.cuenta_cobro_fecha_envio && (
                    <div className="cuenta-cobro-info">
                      <div className="info-envio">
                        <span className="label">Cuenta de cobro enviada:</span>
                        <span className="valor">{formatDateTime(campo.cuenta_cobro_fecha_envio)}</span>
                        {campo.cuenta_cobro_archivo && (
                          <a
                            href={`${UPLOAD_URL}/catalogo/img/comprobantes/${campo.cuenta_cobro_archivo}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="ver-documento"
                          >
                            Ver documento
                          </a>
                        )}
                      </div>
                      {campo.fecha_pago && (
                        <div className="info-pago">
                          <span className="label">Fecha de pago:</span>
                          <span className="valor">{formatDateTime(campo.fecha_pago)}</span>
                          {campo.comprobante_pago_archivo && (
                            <a
                              href={`${UPLOAD_URL}/catalogo/img/comprobantes/${campo.comprobante_pago_archivo}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="ver-documento"
                            >
                              Ver comprobante
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Comprobantes */}
                  {campo.comprobantes?.length > 0 && (
                    <div className="campo-comprobantes">
                      <h4>Comprobantes ({campo.comprobantes.length})</h4>
                      <div className="comprobantes-lista">
                        {campo.comprobantes.map(comp => (
                          <div key={comp.id} className={`comprobante-item ${comp.ruta_archivo ? '' : 'sin-archivo'}`}>
                            <span className="comp-icono">{comp.ruta_archivo ? getFileIcon(comp.tipo_archivo) : '⚠️'}</span>
                            <div className="comp-info">
                              {comp.ruta_archivo ? (
                                <a
                                  href={`${UPLOAD_URL}/catalogo/img/comprobantes/${comp.ruta_archivo}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="comp-nombre"
                                >
                                  {comp.nombre_archivo}
                                </a>
                              ) : (
                                <span className="comp-sin-archivo">
                                  <span className="comp-nombre-muted">Pago sin comprobante adjunto</span>
                                  <label className={`comp-adjuntar-btn ${adjuntandoId === comp.id ? 'cargando' : ''}`}>
                                    {adjuntandoId === comp.id ? 'Subiendo...' : '📎 Adjuntar comprobante'}
                                    <input type="file" hidden accept="image/*,.pdf" disabled={adjuntandoId === comp.id}
                                      onChange={(e) => { adjuntarArchivoComprobante(comp, e.target.files?.[0]); e.target.value = ''; }} />
                                  </label>
                                </span>
                              )}
                              {editDesc.id === comp.id ? (
                                <input
                                  className="comp-desc-input"
                                  value={editDesc.texto}
                                  autoFocus
                                  placeholder="Descripción del pago (ej: Transferencia Bancolombia)"
                                  onChange={(e) => setEditDesc(prev => ({ ...prev, texto: e.target.value }))}
                                  onBlur={(e) => guardarDescripcionComprobante(comp.id, e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); }
                                    if (e.key === 'Escape') { cancelDescRef.current = true; e.target.blur(); }
                                  }}
                                />
                              ) : (
                                <span
                                  className={`comp-descripcion editable ${comp.descripcion ? '' : 'vacia'}`}
                                  title="Clic para editar la descripción"
                                  onClick={() => setEditDesc({ id: comp.id, texto: comp.descripcion || '' })}
                                >
                                  {comp.descripcion || 'Sin descripcion'}
                                  <span className="comp-desc-lapiz">✏️</span>
                                </span>
                              )}
                            </div>
                            <div className="comp-valores">
                              <span className="comp-monto">{formatCurrency(comp.monto)}</span>
                              <span className="comp-fecha">{formatDate(comp.fecha_pago)}</span>
                            </div>
                            <button
                              className="btn-eliminar-comp"
                              onClick={() => eliminarComprobante(comp.id)}
                              title="Eliminar"
                            >
                              X
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Financiero Mejorado */}
      <div className="hv-section">
        <div className="section-header">
          <h2>Informacion Financiera</h2>
        </div>
        <div className="financiero-grid">
          <div className="financiero-item">
            <span className="label">Valor Ejecutado</span>
            <span className="valor">{formatCurrency(hojaVida.valor_ejecutado)}</span>
          </div>
          <div className={`financiero-item ${hojaVida.delta_caja >= 0 ? 'positivo' : 'negativo'}`}>
            <span className="label">Delta en Caja</span>
            <span className="valor">{formatCurrency(hojaVida.delta_caja)}</span>
          </div>
          <div className="financiero-item">
            <span className="label">Descuentos</span>
            {editandoDescuentos ? (
              <div className="descuentos-edit-inline">
                <input
                  type="number"
                  className="input-inline"
                  value={descuentosData}
                  onChange={(e) => setDescuentosData(e.target.value)}
                  min="0"
                  step="1000"
                />
                <div className="descuentos-edit-btns">
                  <button className="btn-accion-inline guardar" onClick={guardarDescuentos}>✓</button>
                  <button className="btn-accion-inline cancelar" onClick={() => setEditandoDescuentos(false)}>✕</button>
                </div>
              </div>
            ) : (
              <span className="valor descuentos clickable" onClick={iniciarEdicionDescuentos} title="Click para editar">
                {formatCurrency(hojaVida.descuentos)}
                <span className="edit-icon">✎</span>
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Historial de Pagos */}
      <div className="hv-section">
        <div className="section-header">
          <h2>Historial de Pagos {hojaVida.pagos_historial?.length > 0 ? `(${hojaVida.pagos_historial.length})` : ''}</h2>
          <button className="btn-agregar-campo" onClick={() => setModalPagoHistorial(true)}>
            + Agregar Pago
          </button>
        </div>
        {hojaVida.pagos_historial && hojaVida.pagos_historial.length > 0 ? (
          <div className="historial-pagos-tabla">
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Fecha</th>
                  <th>Monto</th>
                  <th>Origen</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {hojaVida.pagos_historial.map((pago) => (
                  <tr key={pago.id}>
                    <td>{pago.numero_pago}</td>
                    <td>
                      {editandoPago === pago.id ? (
                        <input
                          type="date"
                          className="input-inline"
                          value={pagoEditData.fecha_pago}
                          onChange={(e) => setPagoEditData(prev => ({ ...prev, fecha_pago: e.target.value }))}
                        />
                      ) : (
                        formatDate(pago.fecha_pago)
                      )}
                    </td>
                    <td className="monto-col">
                      {editandoPago === pago.id ? (
                        <input
                          type="number"
                          className="input-inline input-monto"
                          value={pagoEditData.monto}
                          onChange={(e) => setPagoEditData(prev => ({ ...prev, monto: e.target.value }))}
                          min="0"
                          step="1000"
                        />
                      ) : (
                        formatCurrency(pago.monto)
                      )}
                    </td>
                    <td>
                      <span className={`origen-badge ${pago.origen}`}>
                        {pago.origen === 'excel' ? 'Excel' : 'Manual'}
                      </span>
                    </td>
                    <td className="acciones-col">
                      {editandoPago === pago.id ? (
                        <>
                          <button className="btn-accion-inline guardar" onClick={() => guardarEdicionPago(pago.id)} title="Guardar">✓</button>
                          <button className="btn-accion-inline cancelar" onClick={() => setEditandoPago(null)} title="Cancelar">✕</button>
                        </>
                      ) : (
                        <>
                          <button className="btn-accion-inline editar" onClick={() => iniciarEdicionPago(pago)} title="Editar">✎</button>
                          <button className="btn-accion-inline eliminar" onClick={() => eliminarPagoHistorial(pago.id)} title="Eliminar">🗑</button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan="2"><strong>Total</strong></td>
                  <td className="monto-col">
                    <strong>{formatCurrency(hojaVida.pagos_historial.reduce((s, p) => s + p.monto, 0))}</strong>
                  </td>
                  <td colSpan="2"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        ) : (
          <p className="sin-datos">No hay pagos registrados en el historial</p>
        )}
      </div>

      {/* Seguimiento */}
      <div className="hv-section">
        <div className="section-header">
          <h2>Seguimiento</h2>
          {!editandoSeguimiento ? (
            <button className="btn-editar-campos" onClick={iniciarEdicionSeguimiento}>
              Editar
            </button>
          ) : (
            <div className="campos-acciones">
              <button className="btn-cancelar" onClick={() => setEditandoSeguimiento(false)}>
                Cancelar
              </button>
              <button className="btn-guardar" onClick={guardarSeguimiento} disabled={guardandoSeguimiento}>
                {guardandoSeguimiento ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          )}
        </div>

        {editandoSeguimiento ? (
          <div className="seguimiento-form">
            <div className="form-group">
              <label>Observacion</label>
              <textarea
                value={seguimientoData.observacion}
                onChange={(e) => setSeguimientoData(prev => ({ ...prev, observacion: e.target.value }))}
                rows={3}
                placeholder="Notas de seguimiento..."
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Pago Proxima Semana</label>
                <input
                  type="number"
                  value={seguimientoData.pago_proxima_semana}
                  onChange={(e) => setSeguimientoData(prev => ({ ...prev, pago_proxima_semana: parseFloat(e.target.value) || 0 }))}
                  min="0"
                  step="1000"
                />
              </div>
            </div>
            <div className="form-group">
              <label>Nota Extra</label>
              <textarea
                value={seguimientoData.nota_extra}
                onChange={(e) => setSeguimientoData(prev => ({ ...prev, nota_extra: e.target.value }))}
                rows={2}
                placeholder="Notas adicionales..."
              />
            </div>
          </div>
        ) : (
          <div className="seguimiento-vista">
            <div className="seguimiento-campo">
              <span className="label">Observacion</span>
              <p>{hojaVida.observacion || 'Sin observaciones'}</p>
            </div>
            {hojaVida.pago_proxima_semana > 0 && (
              <div className="seguimiento-campo destacado">
                <span className="label">Pago Proxima Semana</span>
                <span className="valor-destaca">{formatCurrency(hojaVida.pago_proxima_semana)}</span>
              </div>
            )}
            {hojaVida.nota_extra && (
              <div className="seguimiento-campo">
                <span className="label">Nota Extra</span>
                <p>{hojaVida.nota_extra}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Tareas del Equipo */}
      <div className="hv-section">
        <div className="section-header">
          <h2>Tareas del Equipo</h2>
        </div>

        <div className="tareas-agregar">
          <select
            value={nuevaTarea.responsable}
            onChange={(e) => setNuevaTarea(prev => ({ ...prev, responsable: e.target.value }))}
          >
            <option value="">Responsable *</option>
            <option value="Natalia">Natalia</option>
            <option value="Luisa">Luisa</option>
          </select>
          <input
            type="text"
            value={nuevaTarea.tarea}
            onChange={(e) => setNuevaTarea(prev => ({ ...prev, tarea: e.target.value }))}
            placeholder="Descripcion de la tarea..."
            onKeyDown={(e) => e.key === 'Enter' && agregarTarea()}
          />
          <button className="btn-guardar" onClick={agregarTarea} disabled={agregandoTarea}>
            {agregandoTarea ? '...' : 'Agregar'}
          </button>
        </div>

        {hojaVida.tareas && hojaVida.tareas.length > 0 ? (
          <div className="tareas-lista">
            {hojaVida.tareas.map(tarea => (
              <div key={tarea.id} className={`tarea-item tarea-${tarea.estado || 'pendiente'}`}>
                <select
                  className={`tarea-estado-select estado-${tarea.estado || 'pendiente'}`}
                  value={tarea.estado || 'pendiente'}
                  onChange={(e) => cambiarEstadoTarea(tarea.id, e.target.value)}
                >
                  <option value="pendiente">Pendiente</option>
                  <option value="en_progreso">En Progreso</option>
                  <option value="exitosa">Exitosa</option>
                </select>
                <div className="tarea-contenido">
                  {editandoTarea === tarea.id ? (
                    <div className="tarea-edit-inline">
                      <input
                        type="text"
                        className="input-inline tarea-edit-input"
                        value={tareaEditTexto}
                        onChange={(e) => setTareaEditTexto(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') guardarEdicionTarea(tarea.id);
                          if (e.key === 'Escape') setEditandoTarea(null);
                        }}
                        autoFocus
                      />
                      <button className="btn-accion-inline guardar" onClick={() => guardarEdicionTarea(tarea.id)} title="Guardar">✓</button>
                      <button className="btn-accion-inline cancelar" onClick={() => setEditandoTarea(null)} title="Cancelar">✕</button>
                    </div>
                  ) : (
                    <span
                      className={`tarea-texto ${tarea.estado === 'exitosa' ? 'completada-texto' : ''}`}
                      onDoubleClick={() => iniciarEdicionTarea(tarea)}
                      title="Doble click para editar"
                    >
                      {tarea.tarea}
                    </span>
                  )}
                  {(tarea.responsable_nombre || tarea.nombre_responsable) && (
                    <span className="tarea-responsable">
                      {tarea.responsable_nombre || tarea.nombre_responsable}
                    </span>
                  )}
                </div>
                <div className="tarea-acciones">
                  <button className="btn-accion-inline editar" onClick={() => iniciarEdicionTarea(tarea)} title="Editar">✎</button>
                  <button className="btn-accion-inline eliminar" onClick={() => eliminarTarea(tarea.id)} title="Eliminar">X</button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="sin-datos">No hay tareas asignadas</p>
        )}
      </div>

      {/* Control Administrativo */}
      <div className="hv-section">
        <div className="section-header">
          <h2>Control Administrativo</h2>
          {!editandoAdmin ? (
            <button className="btn-editar-campos" onClick={iniciarEdicionAdmin}>
              Editar
            </button>
          ) : (
            <div className="campos-acciones">
              <button className="btn-cancelar" onClick={() => setEditandoAdmin(false)}>
                Cancelar
              </button>
              <button className="btn-guardar" onClick={guardarAdmin} disabled={guardandoAdmin}>
                {guardandoAdmin ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          )}
        </div>

        {editandoAdmin ? (
          <div className="admin-form">
            <div className="form-row">
              <div className="form-group">
                <label>Poliza</label>
                <input type="text" value={adminData.poliza} onChange={(e) => setAdminData(prev => ({ ...prev, poliza: e.target.value }))} />
              </div>
              <div className="form-group">
                <label>Seguro</label>
                <input type="text" value={adminData.seguro} onChange={(e) => setAdminData(prev => ({ ...prev, seguro: e.target.value }))} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Deposito</label>
                <input type="text" value={adminData.deposito} onChange={(e) => setAdminData(prev => ({ ...prev, deposito: e.target.value }))} />
              </div>
              <div className="form-group">
                <label>Contrato Firmado</label>
                <input type="text" value={adminData.contrato_firmado} onChange={(e) => setAdminData(prev => ({ ...prev, contrato_firmado: e.target.value }))} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Llaves</label>
                <input type="text" value={adminData.llaves} onChange={(e) => setAdminData(prev => ({ ...prev, llaves: e.target.value }))} />
              </div>
              <div className="form-group">
                <label>Formatos de Administracion</label>
                <input type="text" value={adminData.formatos_admin} onChange={(e) => setAdminData(prev => ({ ...prev, formatos_admin: e.target.value }))} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Fecha Inicio de Obra</label>
                <input type="date" value={adminData.fecha_inicio_obra} onChange={(e) => setAdminData(prev => ({ ...prev, fecha_inicio_obra: e.target.value }))} />
              </div>
              <div className="form-group">
                <label>Fecha Fin de Obra</label>
                <input type="date" value={adminData.fecha_fin_obra} onChange={(e) => setAdminData(prev => ({ ...prev, fecha_fin_obra: e.target.value }))} />
              </div>
            </div>
          </div>
        ) : (
          <div className="admin-vista">
            <div className="admin-grid">
              <div className="admin-campo">
                <span className="label">Poliza</span>
                <span className="valor">{hojaVida.poliza || '-'}</span>
              </div>
              <div className="admin-campo">
                <span className="label">Seguro</span>
                <span className="valor">{hojaVida.seguro || '-'}</span>
              </div>
              <div className="admin-campo">
                <span className="label">Deposito</span>
                <span className="valor">{hojaVida.deposito || '-'}</span>
              </div>
              <div className="admin-campo">
                <span className="label">Contrato Firmado</span>
                <span className="valor">{hojaVida.contrato_firmado || '-'}</span>
              </div>
              <div className="admin-campo">
                <span className="label">Llaves</span>
                <span className="valor">{hojaVida.llaves || '-'}</span>
              </div>
              <div className="admin-campo">
                <span className="label">Formatos Admin</span>
                <span className="valor">{hojaVida.formatos_admin || '-'}</span>
              </div>
              <div className="admin-campo">
                <span className="label">Inicio de Obra</span>
                <span className="valor">{formatDate(hojaVida.fecha_inicio_obra)}</span>
              </div>
              <div className="admin-campo">
                <span className="label">Fin de Obra</span>
                <span className="valor">{formatDate(hojaVida.fecha_fin_obra)}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal para agregar pago al historial */}
      {modalPagoHistorial && (
        <div className="modal-overlay" onClick={() => setModalPagoHistorial(false)}>
          <div className="modal-comprobante" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Agregar Pago al Historial</h3>
              <button className="btn-cerrar" onClick={() => setModalPagoHistorial(false)}>X</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Monto *</label>
                <input
                  type="number"
                  value={nuevoPago.monto}
                  onChange={(e) => setNuevoPago(prev => ({ ...prev, monto: e.target.value }))}
                  placeholder="0"
                  min="0"
                  step="1000"
                />
              </div>
              <div className="form-group">
                <label>Fecha de Pago</label>
                <input
                  type="date"
                  value={nuevoPago.fecha_pago}
                  onChange={(e) => setNuevoPago(prev => ({ ...prev, fecha_pago: e.target.value }))}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancelar" onClick={() => setModalPagoHistorial(false)}>Cancelar</button>
              <button className="btn-guardar" onClick={agregarPagoHistorial} disabled={agregandoPago}>
                {agregandoPago ? 'Registrando...' : 'Registrar Pago'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Comprobante de Ingresos (documento PDF) */}
      {mostrarComprobanteIngreso && (
        <ComprobanteIngresoModal hojaVida={hojaVida} onClose={() => setMostrarComprobanteIngreso(false)} />
      )}

      {/* Modal para agregar comprobante manual */}
      {mostrarModalComprobante && (
        <div className="modal-overlay" onClick={() => setMostrarModalComprobante(null)}>
          <div className="modal-comprobante" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Registrar Pago</h3>
              <button className="btn-cerrar" onClick={() => setMostrarModalComprobante(null)}>X</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Monto *</label>
                <input
                  type="number"
                  value={comprobanteData.monto}
                  onChange={(e) => setComprobanteData(prev => ({ ...prev, monto: e.target.value }))}
                  placeholder="0"
                  min="0"
                  step="1000"
                />
              </div>
              <div className="form-group">
                <label>Fecha de Pago</label>
                <input
                  type="date"
                  value={comprobanteData.fecha_pago}
                  onChange={(e) => setComprobanteData(prev => ({ ...prev, fecha_pago: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label>Descripcion</label>
                <input
                  type="text"
                  value={comprobanteData.descripcion}
                  onChange={(e) => setComprobanteData(prev => ({ ...prev, descripcion: e.target.value }))}
                  placeholder="Ej: Transferencia bancaria"
                />
              </div>
              <div className="form-group">
                <label>Comprobante (imagen o PDF) — opcional</label>
                <div
                  className={`cartera-dropzone ${dragComprobante ? 'drag-over' : ''}`}
                  onDragOver={(e) => { e.preventDefault(); setDragComprobante(true); }}
                  onDragLeave={() => setDragComprobante(false)}
                  onDrop={handleDropComprobante}
                  onClick={() => fileInputRef.current?.click()}
                  tabIndex={0}
                >
                  {comprobanteData.archivo ? (
                    <div className="cartera-dz-preview">
                      {comprobanteData.archivo.type?.startsWith('image/') && (
                        <img src={URL.createObjectURL(comprobanteData.archivo)} alt="Comprobante" />
                      )}
                      <span className="cartera-dz-nombre">
                        {comprobanteData.archivo.type?.includes('pdf') ? '📄' : '🖼️'} {comprobanteData.archivo.name}
                      </span>
                      <button
                        type="button"
                        className="cartera-dz-quitar"
                        title="Quitar"
                        onClick={(e) => {
                          e.stopPropagation();
                          setComprobanteData(prev => ({ ...prev, archivo: null }));
                          if (fileInputRef.current) fileInputRef.current.value = '';
                        }}
                      >✕</button>
                    </div>
                  ) : (
                    <>
                      <div className="cartera-dz-icon">📎</div>
                      <div className="cartera-dz-text">
                        <strong>Arrastra la imagen aquí</strong>
                        <small>o pégala con <b>Ctrl+V</b> · o haz clic para seleccionar</small>
                      </div>
                    </>
                  )}
                </div>
                <input
                  type="file"
                  ref={fileInputRef}
                  accept="image/*,.pdf"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancelar" onClick={() => setMostrarModalComprobante(null)}>
                Cancelar
              </button>
              <button
                className="btn-guardar"
                onClick={() => subirComprobante(mostrarModalComprobante)}
                disabled={subiendoComprobante === mostrarModalComprobante}
              >
                {subiendoComprobante === mostrarModalComprobante ? 'Subiendo...' : 'Registrar Pago'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para enviar cuenta de cobro */}
      {modalCuentaCobro.visible && (
        <div className="modal-overlay" onClick={() => !enviandoCuentaCobro && setModalCuentaCobro({ visible: false, campo: null })}>
          <div className="modal-cuenta-cobro" onClick={e => e.stopPropagation()}>
            <div className="modal-header cuenta-cobro">
              <h3>Enviar Cuenta de Cobro</h3>
              <button
                className="btn-cerrar"
                onClick={() => !enviandoCuentaCobro && setModalCuentaCobro({ visible: false, campo: null })}
                disabled={enviandoCuentaCobro}
              >
                X
              </button>
            </div>
            <div className="modal-body">
              <div className="info-envio-cuenta">
                <p><strong>Campo:</strong> {modalCuentaCobro.campo?.nombre}</p>
                <p><strong>Valor:</strong> {formatCurrency(modalCuentaCobro.campo?.valor_calculado)}</p>
                <p><strong>Cliente:</strong> {hojaVida.cliente_nombre}</p>
                <p><strong>Se enviará a:</strong> {hojaVida.cliente_email}</p>
              </div>

              <div className="form-group">
                <label>Seleccionar Cuenta de Cobro *</label>
                {cargandoCuentas ? (
                  <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>Cargando cuentas de cobro...</p>
                ) : cuentasCobroDisponibles.length === 0 ? (
                  <p style={{ color: '#dc2626', fontSize: '0.875rem' }}>No hay cuentas de cobro creadas. Créalas primero en Documentos → Cuentas de Cobro.</p>
                ) : (
                  <>
                  <input
                    type="text"
                    placeholder="Buscar por consecutivo, cliente, proyecto..."
                    value={busquedaCuentaCobro}
                    onChange={(e) => setBusquedaCuentaCobro(e.target.value)}
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.85rem', marginBottom: '8px', boxSizing: 'border-box' }}
                  />
                  <div className="lista-cuentas-cobro">
                    {cuentasCobroDisponibles.filter(c => {
                      if (!busquedaCuentaCobro) return true;
                      const q = busquedaCuentaCobro.toLowerCase();
                      return (
                        String(c.consecutivo).padStart(4, '0').includes(q) ||
                        (c.nombre_cliente || '').toLowerCase().includes(q) ||
                        (c.proyecto_nombre || '').toLowerCase().includes(q) ||
                        (c.apartamento || '').toLowerCase().includes(q) ||
                        (c.tipo_pago || '').toLowerCase().includes(q)
                      );
                    }).map(cuenta => (
                      <div
                        key={cuenta.id}
                        className={`cuenta-cobro-item ${cuentaSeleccionada?.id === cuenta.id ? 'seleccionada' : ''}`}
                        onClick={() => setCuentaSeleccionada(cuenta)}
                      >
                        <div className="cuenta-cobro-info">
                          <span className="cuenta-cobro-num">CC-{String(cuenta.consecutivo).padStart(4, '0')}</span>
                          <span className="cuenta-cobro-tipo">{cuenta.tipo_pago?.replace('_', ' ')}</span>
                          <span className="cuenta-cobro-valor">{formatCurrency(cuenta.valor_pago)}</span>
                        </div>
                        <div className="cuenta-cobro-detalle">
                          <span>{cuenta.nombre_cliente} - {cuenta.proyecto_nombre} {cuenta.apartamento ? `Apto ${cuenta.apartamento}` : ''}</span>
                          <span className="cuenta-cobro-fecha">{cuenta.fecha}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  </>
                )}
              </div>

              <div className="alerta-info">
                <p>Al enviar, el cliente recibirá un correo con la cuenta de cobro y el campo quedará en estado "Pendiente de Pago".</p>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn-cancelar"
                onClick={() => setModalCuentaCobro({ visible: false, campo: null })}
                disabled={enviandoCuentaCobro}
              >
                Cancelar
              </button>
              <button
                className="btn-enviar"
                onClick={enviarCuentaCobro}
                disabled={enviandoCuentaCobro || !cuentaSeleccionada}
              >
                {enviandoCuentaCobro ? 'Enviando...' : 'Enviar Cuenta de Cobro'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para marcar como pagado */}
      {modalMarcarPagado.visible && (
        <div className="modal-overlay" onClick={() => !registrandoPago && setModalMarcarPagado({ visible: false, campo: null })}>
          <div className="modal-marcar-pagado" onClick={e => e.stopPropagation()}>
            <div className="modal-header pagado">
              <h3>Registrar Pago</h3>
              <button
                className="btn-cerrar"
                onClick={() => !registrandoPago && setModalMarcarPagado({ visible: false, campo: null })}
                disabled={registrandoPago}
              >
                X
              </button>
            </div>
            <div className="modal-body">
              <div className="info-pago-cuenta">
                <p><strong>Campo:</strong> {modalMarcarPagado.campo?.nombre}</p>
                <p><strong>Valor esperado:</strong> {formatCurrency(modalMarcarPagado.campo?.valor_calculado)}</p>
                <p><strong>Cliente:</strong> {hojaVida.cliente_nombre}</p>
                {modalMarcarPagado.campo?.cuenta_cobro_fecha_envio && (
                  <p><strong>Cuenta enviada:</strong> {formatDateTime(modalMarcarPagado.campo?.cuenta_cobro_fecha_envio)}</p>
                )}
              </div>

              <div className="form-group">
                <label>Comprobante de Pago (PDF o imagen) *</label>
                <input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={(e) => e.target.files[0] && setComprobantePagoArchivo(e.target.files[0])}
                />
                {comprobantePagoArchivo && (
                  <span className="archivo-seleccionado">
                    {comprobantePagoArchivo.name}
                  </span>
                )}
              </div>

              <div className="alerta-info success">
                <p>Al confirmar, se registrara la fecha y hora del pago y el campo quedara en estado "Pagado".</p>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn-cancelar"
                onClick={() => setModalMarcarPagado({ visible: false, campo: null })}
                disabled={registrandoPago}
              >
                Cancelar
              </button>
              <button
                className="btn-confirmar-pago"
                onClick={registrarPagoCuentaCobro}
                disabled={registrandoPago || !comprobantePagoArchivo}
              >
                {registrandoPago ? 'Registrando...' : 'Confirmar Pago'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: generar nueva cuenta de cobro vinculada al campo de cartera */}
      {modalGenerarCobro.visible && (
        <div className="modal-overlay" onClick={() => setModalGenerarCobro({ visible: false, campo: null, fecha: '', tipo_pago: 'avance', valor_pago: 0 })}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Generar Cuenta de Cobro</h2>
              <button className="btn-cerrar" onClick={() => setModalGenerarCobro({ visible: false, campo: null, fecha: '', tipo_pago: 'avance', valor_pago: 0 })}>X</button>
            </div>
            <div className="modal-body">
              <div style={{ background: '#f1f5f9', padding: 12, borderRadius: 8, marginBottom: 16 }}>
                <p style={{ margin: 0 }}><strong>Cliente:</strong> {hojaVida?.cliente_nombre}</p>
                <p style={{ margin: 0 }}><strong>Proyecto:</strong> {hojaVida?.proyecto_nombre}</p>
                <p style={{ margin: 0 }}><strong>Campo:</strong> {modalGenerarCobro.campo?.nombre} ({modalGenerarCobro.campo?.porcentaje}%)</p>
              </div>
              <div className="form-group">
                <label>Fecha *</label>
                <input
                  type="date"
                  value={modalGenerarCobro.fecha}
                  onChange={(e) => setModalGenerarCobro(prev => ({ ...prev, fecha: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label>Tipo de pago *</label>
                <select
                  value={modalGenerarCobro.tipo_pago}
                  onChange={(e) => setModalGenerarCobro(prev => ({ ...prev, tipo_pago: e.target.value }))}
                >
                  <option value="separacion">Separación</option>
                  <option value="anticipo">Anticipo</option>
                  <option value="avance">Avance de obra</option>
                  <option value="entrega_final">Entrega final</option>
                </select>
              </div>
              <div className="form-group">
                <label>Valor a cobrar *</label>
                <input
                  type="number"
                  value={modalGenerarCobro.valor_pago}
                  onChange={(e) => setModalGenerarCobro(prev => ({ ...prev, valor_pago: e.target.value }))}
                  step="1"
                  min="0"
                />
                <small style={{ color: '#64748b' }}>Por defecto, el saldo pendiente del campo. Puedes ajustarlo si vas a cobrar un monto distinto.</small>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancelar" onClick={() => setModalGenerarCobro({ visible: false, campo: null, fecha: '', tipo_pago: 'avance', valor_pago: 0 })}>
                Cancelar
              </button>
              <button
                className="btn-confirmar"
                onClick={confirmarGenerarCobro}
                disabled={generandoCobro}
              >
                {generandoCobro ? 'Generando...' : 'Generar y vincular'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HojaVidaDetalle;
